/** Was die Funktion braucht: eine Kennung. Ob `Task` oder `TaskEntryData`, ist ihr gleich. */
interface HatKennung { id: string }

/** Eine Zeile samt ihrer gebuchten **Fokuszeit**. */
export type TaskWithFocus<T extends HatKennung = HatKennung> = T & { focusedSeconds: number | null }

/**
 * **Die Fokuszeit an die Aufgabe hängen, ohne jede Zeile neu zu bauen.**
 *
 * Die Zeiten kommen aus einer Live-Abfrage und damit bei jedem Feuern als
 * neue Map — auch wenn sich keine einzige Zahl geändert hat. Wer daraufhin
 * `{ ...task, focusedSeconds }` für alle rechnet, gibt jeder Zeile ein neues
 * Objekt, und das `React.memo` an `TaskEntry` greift bei keiner.
 *
 * Gemessen am 21.09.2026 (`render-audit`): Nach dem Anlegen einer Aufgabe
 * rendern so alle 34 Zeilen neu — der Profiler meldete Commits über 200ms.
 *
 * Der Zwischenspeicher hängt an der Aufgabe selbst (`WeakMap`): Kommt
 * dieselbe Aufgabe mit derselben Zahl wieder, kommt auch dasselbe Objekt
 * zurück. Neue Werte bekommen ein neues — mehr ändert sich nicht.
 */
export function withFocusSeconds<T extends HatKennung>(
  tasks: readonly T[],
  seconds: Map<string, number> | undefined,
  cache: WeakMap<T & object, TaskWithFocus<T>>,
): readonly T[] {
  if (!seconds) return tasks

  return tasks.map(task => {
    const value = seconds.get(task.id) ?? null
    const cached = cache.get(task)
    if (cached && cached.focusedSeconds === value) return cached
    const next = { ...task, focusedSeconds: value } as TaskWithFocus<T>
    cache.set(task, next)
    return next
  })
}
